import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useMeeting } from "plg/meeting/MeetingContext";

interface Props {
  /** The server's SOT message, e.g. "You have hit today's viewing limit. It resets tomorrow." */
  message: string;
  requestAHireRoute: string;
  /** Where the ✕ goes (the talent list). */
  closeRoute?: string;
  /** Shown over the page behind it (Browse), blurred, instead of in place of a profile. */
  overlay?: boolean;
  /** Overlay: the ✕ closes the wall. */
  onClose?: () => void;
}

/** First sentence as the heading, the rest underneath (the SOT gives one message per wall). */
const split = (message: string) => {
  const i = message.indexOf(". ");
  return i < 0 ? [message, ""] : [message.slice(0, i + 1), message.slice(i + 2)];
};

// PLG wall copy (design): one message for every cap, pointing at verification.
const PLG_TITLE = "That's a lot of profiles";
const PLG_BODY = "Talk to us to verify your business and unlock more profiles.";

const closeIcon = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** Shown instead of a profile when a PLG view cap is reached. Request a hire is the action on every wall. */
function ViewLimitWall({ message, requestAHireRoute, closeRoute, overlay = false, onClose }: Props) {
  // PLG: one CTA everywhere, Request a meeting (opens the panel); the portal keeps Request a hire.
  const meeting = useMeeting();
  const [title, body] = meeting ? [PLG_TITLE, PLG_BODY] : split(message);
  // Overlay covers only the page it sits in: the sidebar and top bar stay sharp.
  const rootRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState<CSSProperties>({});
  useLayoutEffect(() => {
    const host = overlay ? rootRef.current?.parentElement : null;
    if (!host) return;
    const measure = () => setArea({ left: Math.max(0, host.getBoundingClientRect().left) });
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(host);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [overlay]);
  return (
    <div
      ref={rootRef}
      className={`view-limit-wall${overlay ? " view-limit-wall--overlay" : ""}`}
      style={overlay ? area : undefined}
      role="alert"
    >
      {/* In place of a profile: placeholder rows only, blurred behind the card (no talent data past the cap). */}
      {!overlay && (
      <div className="view-limit-wall__backdrop" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div className="view-limit-wall__ghost" key={row}>
            <span className="view-limit-wall__ghost-avatar" />
            <span className="view-limit-wall__ghost-lines">
              <span />
              <span />
              <span />
            </span>
            <span className="view-limit-wall__ghost-actions">
              <span />
              <span />
            </span>
          </div>
        ))}
      </div>
      )}
      <div className="view-limit-wall__card">
        {onClose ? (
          <button type="button" className="view-limit-wall__close" onClick={onClose} aria-label="Close">
            {closeIcon}
          </button>
        ) : (
          closeRoute && (
            <Link className="view-limit-wall__close" to={closeRoute} aria-label="Close">
              {closeIcon}
            </Link>
          )
        )}
        <span className="view-limit-wall__icon" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <h2>{title}</h2>
        {body && <p>{body}</p>}
        <div className="view-limit-wall__actions">
          {meeting ? (
            <button
              type="button"
              className="view-limit-wall__btn"
              onClick={() => meeting.open("cap_wall")}
              disabled={meeting.sent}
            >
              {meeting.sent ? "Meeting requested" : "Request a meeting"}
            </button>
          ) : (
            <Link className="view-limit-wall__btn" to={requestAHireRoute}>
              Request a hire
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewLimitWall;
